import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "../components/DashboardLayout";
import { db } from "../firebase";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import {
  formatDate,
  formatDateForInput,
  parseDateString,
} from "../utils/dateUtils";
import { createNotification } from "../utils/notifications";

function Vehicles() {
  const { t } = useTranslation();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);
  const [viewingVehicle, setViewingVehicle] = useState(null);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  const [formData, setFormData] = useState({
    licensePlate: "",
    sixMonthInspection: "", // 6 mujorshi
    registrationDate: "", // regjistrimi
    type: "",
    model: "",
    year: "",
    transmission: "", // Manual or Automatic
    color: "",
    comments: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Search and Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterDateType, setFilterDateType] = useState("registrationDate"); // registrationDate or sixMonthInspection
  const [sortBy, setSortBy] = useState("newest-first");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 30;

  // Fetch vehicles from Firestore
  const fetchVehicles = async () => {
    try {
      const q = query(collection(db, "vehicles"), orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);
      const vehiclesData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setVehicles(vehiclesData);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      showToast(t("vehicles.toast.fetchError"), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check for 6-month inspection and registration date due within 7 days (same logic as instructors)
  const checkExpiringVehicleDates = useCallback(async () => {
    try {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const today = new Date(now);

      const getDaysUntilDate = (dateStr) => {
        const d = new Date(dateStr);
        d.setHours(0, 0, 0, 0);
        const diffTime = d - today;
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      };

      const notificationExistsForToday = async (type, entityId) => {
        const todayStart = new Date(today);
        todayStart.setHours(0, 0, 0, 0);
        const todayEnd = new Date(today);
        todayEnd.setHours(23, 59, 59, 999);

        const existingNotifications = await getDocs(
          query(
            collection(db, "notifications"),
            where("type", "==", type),
            where("entityId", "==", entityId)
          )
        );

        for (const docSnap of existingNotifications.docs) {
          const notifData = docSnap.data();
          if (notifData.createdAt) {
            const createdAt = notifData.createdAt.toDate
              ? notifData.createdAt.toDate()
              : new Date(notifData.createdAt);
            if (createdAt >= todayStart && createdAt <= todayEnd) {
              return true;
            }
          }
        }
        return false;
      };

      for (const vehicle of vehicles) {
        const vehicleName = vehicle.licensePlate || `${vehicle.model || ""} ${vehicle.type || ""}`.trim() || "Vehicle";

        // 6-month inspection (6 mujorshi) due within 7 days
        if (vehicle.sixMonthInspection) {
          const daysUntil = getDaysUntilDate(vehicle.sixMonthInspection);
          if (daysUntil >= 0 && daysUntil <= 7) {
            const existsToday = await notificationExistsForToday(
              "vehicle_six_month_inspection_due",
              vehicle.id
            );
            if (!existsToday) {
              await createNotification(
                "vehicle_six_month_inspection_due",
                "vehicle",
                vehicle.id,
                vehicleName,
                {
                  dueDate: vehicle.sixMonthInspection,
                  documentType: "six_month_inspection",
                  daysUntilDue: daysUntil,
                }
              );
            }
          }
        }

        // Registration date (data e regjistrimit) due within 7 days
        if (vehicle.registrationDate) {
          const daysUntil = getDaysUntilDate(vehicle.registrationDate);
          if (daysUntil >= 0 && daysUntil <= 7) {
            const existsToday = await notificationExistsForToday(
              "vehicle_registration_due",
              vehicle.id
            );
            if (!existsToday) {
              await createNotification(
                "vehicle_registration_due",
                "vehicle",
                vehicle.id,
                vehicleName,
                {
                  dueDate: vehicle.registrationDate,
                  documentType: "registration",
                  daysUntilDue: daysUntil,
                }
              );
            }
          }
        }
      }
    } catch (error) {
      console.error("Error checking vehicle due dates:", error);
    }
  }, [vehicles]);

  useEffect(() => {
    if (vehicles.length > 0) {
      checkExpiringVehicleDates();
    }
  }, [vehicles, checkExpiringVehicleDates]);

  // Validation
  const validateField = (name, value) => {
    let error = "";

    const requiredFields = [
      "licensePlate",
      "sixMonthInspection",
      "registrationDate",
      "type",
      "model",
      "year",
      "transmission",
    ];

    if (requiredFields.includes(name) && !value) {
      error = t("vehicles.validation.required");
    }

    // Year validation
    if (name === "year" && value) {
      const yearNum = parseInt(value);
      const currentYear = new Date().getFullYear();
      if (isNaN(yearNum) || yearNum < 1900 || yearNum > currentYear + 1) {
        error = t("vehicles.validation.yearInvalid");
      }
    }

    // License plate validation
    if (name === "licensePlate" && value && value.length < 2) {
      error = t("vehicles.validation.licensePlateLength");
    }

    return error;
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (touched[name]) {
      const error = validateField(name, value);
      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }
  };

  // Handle input blur
  const handleBlur = (e) => {
    const { name, value } = e.target;

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      "licensePlate",
      "sixMonthInspection",
      "registrationDate",
      "type",
      "model",
      "year",
      "transmission",
    ];

    requiredFields.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);
    setTouched(
      requiredFields.reduce((acc, field) => ({ ...acc, [field]: true }), {})
    );

    return Object.keys(newErrors).length === 0;
  };

  // Show toast notification
  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showToast(t("vehicles.toast.fillRequired"), "error");
      return;
    }

    try {
      if (editingVehicle) {
        // Update existing vehicle
        const vehicleRef = doc(db, "vehicles", editingVehicle.id);
        await updateDoc(vehicleRef, {
          ...formData,
          updatedAt: serverTimestamp(),
        });

        await createNotification(
          "vehicle_updated",
          "vehicle",
          editingVehicle.id,
          formData.licensePlate
        );
        showToast(t("vehicles.toast.updated"), "success");
      } else {
        // Create new vehicle
        const docRef = await addDoc(collection(db, "vehicles"), {
          ...formData,
          createdAt: serverTimestamp(),
        });

        await createNotification(
          "vehicle_added",
          "vehicle",
          docRef.id,
          formData.licensePlate
        );
        showToast(t("vehicles.toast.created"), "success");
      }

      // Refresh vehicles list
      fetchVehicles();
      handleCloseModal();
    } catch (error) {
      console.error("Error saving vehicle:", error);
      showToast(t("vehicles.toast.saveError"), "error");
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!vehicleToDelete) return;

    try {
      await deleteDoc(doc(db, "vehicles", vehicleToDelete.id));
      await createNotification(
        "vehicle_deleted",
        "vehicle",
        vehicleToDelete.id,
        vehicleToDelete.licensePlate
      );
      showToast(t("vehicles.toast.deleted"), "success");
      fetchVehicles();
      setIsDeleteModalOpen(false);
      setVehicleToDelete(null);
    } catch (error) {
      console.error("Error deleting vehicle:", error);
      showToast(t("vehicles.toast.deleteError"), "error");
    }
  };

  // Handle edit
  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      licensePlate: vehicle.licensePlate || "",
      sixMonthInspection: vehicle.sixMonthInspection
        ? formatDateForInput(vehicle.sixMonthInspection)
        : "",
      registrationDate: vehicle.registrationDate
        ? formatDateForInput(vehicle.registrationDate)
        : "",
      type: vehicle.type || "",
      model: vehicle.model || "",
      year: vehicle.year || "",
      transmission: vehicle.transmission || "",
      color: vehicle.color || "",
      comments: vehicle.comments || "",
    });
    setIsModalOpen(true);
  };

  // Handle view
  const handleView = (vehicle) => {
    setViewingVehicle(vehicle);
    setIsViewModalOpen(true);
  };

  // Handle add new
  const handleAddNew = () => {
    setEditingVehicle(null);
    setFormData({
      licensePlate: "",
      sixMonthInspection: "",
      registrationDate: "",
      type: "",
      model: "",
      year: "",
      transmission: "",
      color: "",
      comments: "",
    });
    setErrors({});
    setTouched({});
    setIsModalOpen(true);
  };

  // Handle close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingVehicle(null);
    setFormData({
      licensePlate: "",
      sixMonthInspection: "",
      registrationDate: "",
      type: "",
      model: "",
      year: "",
      transmission: "",
      color: "",
      comments: "",
    });
    setErrors({});
    setTouched({});
  };

  // Get unique vehicle types for filter
  const uniqueVehicleTypes = useMemo(() => {
    const types = vehicles
      .map((vehicle) => vehicle.type)
      .filter((type) => type);
    return [...new Set(types)].sort();
  }, [vehicles]);

  // Get unique years for filter
  const uniqueYears = useMemo(() => {
    const years = vehicles
      .map((vehicle) => vehicle.year)
      .filter((year) => year)
      .map((year) => parseInt(year))
      .filter((year) => !isNaN(year));
    return [...new Set(years)].sort((a, b) => b - a); // Sort descending (newest first)
  }, [vehicles]);

  // Calculate stats
  const stats = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thirtyDaysFromNow = new Date(today);
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

    // Total vehicles
    const total = vehicles.length;

    // Count manual and automatic vehicles
    let manualCount = 0;
    let automaticCount = 0;

    vehicles.forEach((vehicle) => {
      const transmission = (vehicle.transmission || "").toLowerCase();
      if (transmission === "manual") {
        manualCount++;
      } else if (transmission === "automatic") {
        automaticCount++;
      }
    });

    // Vehicles added this year
    const thisYearStart = new Date(now.getFullYear(), 0, 1);
    let vehiclesThisYear = 0;
    vehicles.forEach((vehicle) => {
      if (vehicle.createdAt) {
        const createdAt = vehicle.createdAt.toDate
          ? vehicle.createdAt.toDate()
          : new Date(vehicle.createdAt.seconds * 1000);
        if (createdAt >= thisYearStart) {
          vehiclesThisYear++;
        }
      }
    });

    return {
      total,
      manual: manualCount,
      automatic: automaticCount,
      vehiclesThisYear,
    };
  }, [vehicles]);

  // Filter and search vehicles
  const filteredAndSortedVehicles = useMemo(() => {
    let filtered = [...vehicles];

    // Search filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (vehicle) =>
          vehicle.licensePlate?.toLowerCase().includes(searchLower) ||
          vehicle.type?.toLowerCase().includes(searchLower) ||
          vehicle.model?.toLowerCase().includes(searchLower) ||
          vehicle.year?.toString().includes(searchLower)
      );
    }

    // Type filter
    if (filterType) {
      filtered = filtered.filter((vehicle) => vehicle.type === filterType);
    }

    // Year filter
    if (filterYear) {
      filtered = filtered.filter(
        (vehicle) => vehicle.year?.toString() === filterYear
      );
    }

    // Date range filter
    if (filterDateFrom || filterDateTo) {
      filtered = filtered.filter((vehicle) => {
        const dateField = vehicle[filterDateType];
        if (!dateField) return false;

        const vehicleDate = new Date(dateField);
        if (isNaN(vehicleDate.getTime())) return false;

        // Set time to start of day for comparison
        const vehicleDateOnly = new Date(
          vehicleDate.getFullYear(),
          vehicleDate.getMonth(),
          vehicleDate.getDate()
        );

        if (filterDateFrom) {
          const fromDate = parseDateString(filterDateFrom);
          if (fromDate) {
            fromDate.setHours(0, 0, 0, 0);
            if (vehicleDateOnly < fromDate) return false;
          }
        }

        if (filterDateTo) {
          const toDate = parseDateString(filterDateTo);
          if (toDate) {
            toDate.setHours(23, 59, 59, 999);
            if (vehicleDateOnly > toDate) return false;
          }
        }

        return true;
      });
    }

    // Sorting
    filtered.sort((a, b) => {
      if (sortBy === "newest-first") {
        const aDate = a.createdAt
          ? new Date(a.createdAt.seconds * 1000)
          : new Date(0);
        const bDate = b.createdAt
          ? new Date(b.createdAt.seconds * 1000)
          : new Date(0);
        return bDate - aDate;
      } else if (sortBy === "oldest-first") {
        const aDate = a.createdAt
          ? new Date(a.createdAt.seconds * 1000)
          : new Date(0);
        const bDate = b.createdAt
          ? new Date(b.createdAt.seconds * 1000)
          : new Date(0);
        return aDate - bDate;
      } else if (sortBy === "licensePlate-az") {
        return (a.licensePlate || "").localeCompare(b.licensePlate || "");
      } else if (sortBy === "licensePlate-za") {
        return (b.licensePlate || "").localeCompare(a.licensePlate || "");
      } else if (sortBy === "year-high") {
        return parseInt(b.year || 0) - parseInt(a.year || 0);
      } else if (sortBy === "year-low") {
        return parseInt(a.year || 0) - parseInt(b.year || 0);
      } else if (sortBy === "type-az") {
        return (a.type || "").localeCompare(b.type || "");
      } else if (sortBy === "type-za") {
        return (b.type || "").localeCompare(a.type || "");
      }
      return 0;
    });

    return filtered;
  }, [
    vehicles,
    searchQuery,
    filterType,
    filterYear,
    filterDateFrom,
    filterDateTo,
    filterDateType,
    sortBy,
  ]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedVehicles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedVehicles = filteredAndSortedVehicles.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchQuery,
    filterType,
    filterYear,
    filterDateFrom,
    filterDateTo,
    filterDateType,
    sortBy,
  ]);

  // Clear all filters
  const handleClearFilters = () => {
    setSearchQuery("");
    setFilterType("");
    setFilterYear("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setSortBy("newest-first");
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t("vehicles.title")}
            </h1>
            <p className="text-gray-600 mt-1">{t("vehicles.subtitle")}</p>
          </div>
          <button
            onClick={handleAddNew}
            className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            {t("vehicles.addVehicle")}
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Manual Cars */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-4">
              {t("vehicles.stats.manual")}
            </h3>
            <div className="flex items-baseline">
              <span className="text-3xl font-bold text-gray-900">
                {stats.manual}
              </span>
              <span className="ml-2 text-sm text-gray-500">
                {t("vehicles.stats.vehicles")}
              </span>
            </div>
          </div>

          {/* Automatic Cars */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-4">
              {t("vehicles.stats.automatic")}
            </h3>
            <div className="flex items-baseline">
              <span className="text-3xl font-bold text-gray-900">
                {stats.automatic}
              </span>
              <span className="ml-2 text-sm text-gray-500">
                {t("vehicles.stats.vehicles")}
              </span>
            </div>
          </div>

          {/* Total Vehicles */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-4">
              {t("vehicles.stats.totalVehicles")}
            </h3>
            <div className="flex items-baseline">
              <span className="text-3xl font-bold text-gray-900">
                {stats.total}
              </span>
              <span className="ml-2 text-sm text-gray-500">
                {t("vehicles.stats.vehicles")}
              </span>
            </div>
          </div>

          {/* Vehicles This Year */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-4">
              {t("vehicles.stats.thisYear")}
            </h3>
            <div className="flex items-baseline">
              <span className="text-3xl font-bold text-gray-900">
                {stats.vehiclesThisYear}
              </span>
              <span className="ml-2 text-sm text-gray-500">
                {t("vehicles.stats.vehicles")}
              </span>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow p-4 space-y-3">
          {/* Search Bar Row */}
          <div className="relative">
            <input
              type="text"
              placeholder={t("vehicles.search")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border-2 border-gray-400 rounded-lg bg-gray-50 font-semibold text-gray-900 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-black/25 focus:border-black text-base"
            />
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>

          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Sort By */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
            >
              <option value="newest-first">
                {t("vehicles.sort.newestFirst")}
              </option>
              <option value="oldest-first">
                {t("vehicles.sort.oldestFirst")}
              </option>
              <option value="licensePlate-az">
                {t("vehicles.sort.licensePlateAZ")}
              </option>
              <option value="licensePlate-za">
                {t("vehicles.sort.licensePlateZA")}
              </option>
              <option value="year-high">{t("vehicles.sort.yearHigh")}</option>
              <option value="year-low">{t("vehicles.sort.yearLow")}</option>
              <option value="type-az">{t("vehicles.sort.typeAZ")}</option>
              <option value="type-za">{t("vehicles.sort.typeZA")}</option>
            </select>

            {/* Type Filter */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
            >
              <option value="">{t("vehicles.filters.allTypes")}</option>
              {uniqueVehicleTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>

            {/* Year Filter */}
            <select
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
            >
              <option value="">{t("vehicles.filters.allYears")}</option>
              {uniqueYears.map((year) => (
                <option key={year} value={year.toString()}>
                  {year}
                </option>
              ))}
            </select>

            {/* Date Type Filter */}
            <select
              value={filterDateType}
              onChange={(e) => setFilterDateType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
            >
              <option value="registrationDate">
                {t("vehicles.filters.registrationDate")}
              </option>
              <option value="sixMonthInspection">
                {t("vehicles.filters.sixMonthInspection")}
              </option>
            </select>

            {/* Date From Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                {t("vehicles.filters.dateFrom")}:
              </label>
              <input
                type="date"
                value={
                  filterDateFrom
                    ? formatDateForInput(
                        parseDateString(filterDateFrom) || filterDateFrom
                      )
                    : ""
                }
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) {
                    const date = new Date(value);
                    setFilterDateFrom(formatDate(date));
                  } else {
                    setFilterDateFrom("");
                  }
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
              />
            </div>

            {/* Date To Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                {t("vehicles.filters.dateTo")}:
              </label>
              <input
                type="date"
                value={
                  filterDateTo
                    ? formatDateForInput(
                        parseDateString(filterDateTo) || filterDateTo
                      )
                    : ""
                }
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) {
                    const date = new Date(value);
                    // Validate that "to date" is not before "from date"
                    if (filterDateFrom) {
                      const fromDate = parseDateString(filterDateFrom);
                      if (fromDate && date < fromDate) {
                        return; // Don't update if invalid
                      }
                    }
                    setFilterDateTo(formatDate(date));
                  } else {
                    setFilterDateTo("");
                  }
                }}
                min={
                  filterDateFrom
                    ? formatDateForInput(
                        parseDateString(filterDateFrom) || filterDateFrom
                      )
                    : undefined
                }
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
              />
            </div>

            {/* Clear Filters */}
            {(searchQuery ||
              filterType ||
              filterYear ||
              filterDateFrom ||
              filterDateTo ||
              sortBy !== "newest-first") && (
              <button
                onClick={handleClearFilters}
                className="ml-auto px-3 py-2 text-sm text-gray-600 hover:text-black hover:bg-gray-100 rounded-lg transition-colors flex items-center space-x-1"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                <span>{t("vehicles.clearAll")}</span>
              </button>
            )}
          </div>
        </div>

        {/* Vehicles Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">
              {t("vehicles.loading")}
            </div>
          ) : filteredAndSortedVehicles.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <svg
                className="w-16 h-16 mb-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0"
                />
              </svg>
              <p>
                {searchQuery
                  ? t("vehicles.noVehiclesFound")
                  : t("vehicles.noVehicles")}
              </p>
              {!searchQuery && (
                <button
                  onClick={handleAddNew}
                  className="mt-4 text-black hover:underline"
                >
                  {t("vehicles.addFirstVehicle")}
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("vehicles.table.licensePlate")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("vehicles.table.type")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("vehicles.table.model")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("vehicles.table.year")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("vehicles.table.transmission")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("vehicles.table.sixMonthInspection")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("vehicles.table.registrationDate")}
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("vehicles.table.actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedVehicles.map((vehicle) => (
                      <tr
                        key={vehicle.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {vehicle.licensePlate}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {vehicle.type}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {vehicle.model}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {vehicle.year}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {vehicle.transmission
                              ? vehicle.transmission === "manual"
                                ? t("vehicles.form.manual")
                                : t("vehicles.form.automatic")
                              : "-"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {vehicle.sixMonthInspection
                              ? formatDate(vehicle.sixMonthInspection)
                              : "-"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {vehicle.registrationDate
                              ? formatDate(vehicle.registrationDate)
                              : "-"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleView(vehicle)}
                              className="text-blue-600 hover:text-blue-900 transition-colors"
                              title={t("vehicles.actions.view")}
                            >
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleEdit(vehicle)}
                              className="text-indigo-600 hover:text-indigo-900 transition-colors"
                              title={t("vehicles.actions.edit")}
                            >
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                            </button>
                            <button
                              onClick={() => {
                                setVehicleToDelete(vehicle);
                                setIsDeleteModalOpen(true);
                              }}
                              className="text-red-600 hover:text-red-900 transition-colors"
                              title={t("vehicles.actions.delete")}
                            >
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-200">
                  <div className="text-sm text-gray-700">
                    {t("vehicles.showing")}{" "}
                    <span className="font-medium">
                      {startIndex + 1}-
                      {Math.min(
                        startIndex + itemsPerPage,
                        filteredAndSortedVehicles.length
                      )}
                    </span>{" "}
                    {t("vehicles.of")}{" "}
                    <span className="font-medium">
                      {filteredAndSortedVehicles.length}
                    </span>{" "}
                    {t("vehicles.total")}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      {t("common.previous")}
                    </button>
                    <button
                      onClick={() =>
                        setCurrentPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      {t("common.next")}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {editingVehicle
                  ? t("vehicles.editVehicle")
                  : t("vehicles.addVehicle")}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* License Plate */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("vehicles.form.licensePlate")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="licensePlate"
                    value={formData.licensePlate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black ${
                      errors.licensePlate ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.licensePlate && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.licensePlate}
                    </p>
                  )}
                </div>

                {/* Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("vehicles.form.type")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black ${
                      errors.type ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.type && (
                    <p className="mt-1 text-sm text-red-500">{errors.type}</p>
                  )}
                </div>

                {/* Model */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("vehicles.form.model")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black ${
                      errors.model ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.model && (
                    <p className="mt-1 text-sm text-red-500">{errors.model}</p>
                  )}
                </div>

                {/* Year */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("vehicles.form.year")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="year"
                    value={formData.year}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    min="1900"
                    max={new Date().getFullYear() + 1}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black ${
                      errors.year ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.year && (
                    <p className="mt-1 text-sm text-red-500">{errors.year}</p>
                  )}
                </div>

                {/* Transmission */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("vehicles.form.transmission")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="transmission"
                    value={formData.transmission}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black ${
                      errors.transmission ? "border-red-500" : "border-gray-300"
                    }`}
                  >
                    <option value="">
                      {t("vehicles.form.selectTransmission")}
                    </option>
                    <option value="manual">{t("vehicles.form.manual")}</option>
                    <option value="automatic">
                      {t("vehicles.form.automatic")}
                    </option>
                  </select>
                  {errors.transmission && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.transmission}
                    </p>
                  )}
                </div>

                {/* Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("vehicles.form.color")}
                  </label>
                  <input
                    type="text"
                    name="color"
                    value={formData.color}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={t("vehicles.form.colorPlaceholder")}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black ${
                      errors.color ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.color && (
                    <p className="mt-1 text-sm text-red-500">{errors.color}</p>
                  )}
                </div>

                {/* 6 Month Inspection & Registration Date - same row */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("vehicles.form.sixMonthInspection")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="sixMonthInspection"
                    value={formData.sixMonthInspection}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black ${
                      errors.sixMonthInspection
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                  />
                  {errors.sixMonthInspection && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.sixMonthInspection}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("vehicles.form.registrationDate")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="registrationDate"
                    value={formData.registrationDate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black ${
                      errors.registrationDate
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                  />
                  {errors.registrationDate && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.registrationDate}
                    </p>
                  )}
                </div>

                {/* Comments - full width on bottom */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("vehicles.form.comments")}
                  </label>
                  <textarea
                    name="comments"
                    value={formData.comments}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    rows={3}
                    placeholder={t("vehicles.form.commentsPlaceholder")}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black resize-y"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t("vehicles.form.cancel")}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                >
                  {editingVehicle
                    ? t("vehicles.form.updateVehicle")
                    : t("vehicles.form.createVehicle")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {isViewModalOpen && viewingVehicle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">
                {t("vehicles.view.title")}
              </h2>
              <button
                onClick={() => setIsViewModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    {t("vehicles.view.licensePlate")}
                  </p>
                  <p className="mt-1 text-sm text-gray-900">
                    {viewingVehicle.licensePlate}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    {t("vehicles.view.type")}
                  </p>
                  <p className="mt-1 text-sm text-gray-900">
                    {viewingVehicle.type}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    {t("vehicles.view.model")}
                  </p>
                  <p className="mt-1 text-sm text-gray-900">
                    {viewingVehicle.model}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    {t("vehicles.view.year")}
                  </p>
                  <p className="mt-1 text-sm text-gray-900">
                    {viewingVehicle.year}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    {t("vehicles.view.transmission")}
                  </p>
                  <p className="mt-1 text-sm text-gray-900">
                    {viewingVehicle.transmission
                      ? viewingVehicle.transmission === "manual"
                        ? t("vehicles.form.manual")
                        : t("vehicles.form.automatic")
                      : "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    {t("vehicles.view.color")}
                  </p>
                  <p className="mt-1 text-sm text-gray-900">
                    {viewingVehicle.color || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    {t("vehicles.view.sixMonthInspection")}
                  </p>
                  <p className="mt-1 text-sm text-gray-900">
                    {viewingVehicle.sixMonthInspection
                      ? formatDate(viewingVehicle.sixMonthInspection)
                      : "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    {t("vehicles.view.registrationDate")}
                  </p>
                  <p className="mt-1 text-sm text-gray-900">
                    {viewingVehicle.registrationDate
                      ? formatDate(viewingVehicle.registrationDate)
                      : "-"}
                  </p>
                </div>
                {viewingVehicle.comments && (
                  <div className="col-span-2">
                    <p className="text-sm font-medium text-gray-500">
                      {t("vehicles.view.comments")}
                    </p>
                    <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
                      {viewingVehicle.comments}
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setIsViewModalOpen(false)}
                className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                {t("vehicles.view.close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && vehicleToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {t("vehicles.delete.title")}
              </h2>
            </div>
            <div className="p-6">
              <p className="text-gray-700">
                {t("vehicles.delete.message")}{" "}
                <span className="font-semibold">
                  {vehicleToDelete.licensePlate}
                </span>
                ? {t("vehicles.delete.cannotUndo")}
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setVehicleToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t("vehicles.delete.cancel")}
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                {t("vehicles.delete.delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.show && (
        <div
          className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${
            toast.type === "error"
              ? "bg-red-500 text-white"
              : "bg-green-500 text-white"
          }`}
        >
          {toast.message}
        </div>
      )}
    </DashboardLayout>
  );
}

export default Vehicles;
